import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import type { Locale } from '@nexa/content';
import { revealAll } from '@nexa/ui';
import { ContactDialogProvider } from '@/core/contact';
import { LocaleProvider, assertServedLocale, useCopy } from '@/core/i18n';
import { Footer } from './Footer.tsx';
import { Header } from './Header.tsx';
import { useHashScroll } from './useHashScroll.ts';

/**
 * Khung chung của mọi trang trong MỘT ngôn ngữ. `locale` là hằng số của cả nhánh cây route —
 * không phải state, không đọc từ trình duyệt. Từ 2026-08-30 nó đến từ `?lang` của URL
 * (`localeFromSearch` ở `core/router/routes.tsx`), không còn từ prefix path.
 */
export function RootLayout({ locale }: { readonly locale: Locale }) {
  // Lưới cho ca nginx phục vụ file của ngôn ngữ này cho URL của ngôn ngữ kia. Chạy trong effect
  // vì nó chỉ có nghĩa trên trình duyệt; giá trị nó so đã được chụp từ trước khi React render.
  useEffect(() => {
    assertServedLocale(locale);
  }, [locale]);

  return (
    <LocaleProvider locale={locale}>
      <ContactDialogProvider>
        <LayoutShell />
      </ContactDialogProvider>
    </LocaleProvider>
  );
}

function LayoutShell() {
  useHashScroll();
  const copy = useCopy();

  /*
   * Gắn reveal MỘT LẦN cho cả trang. Đặt ở layout chứ không ở từng section vì `revealAll` là
   * controller mức DOM — nó quét theo thuộc tính đánh dấu, kể cả node do section không hydrate
   * render ra. Lúc effect này chạy thì nội dung trang đã có trong DOM: vite-react-ssg await
   * xong mọi route `lazy` khớp URL rồi mới hydrate, nên cả cây vào DOM trong CÙNG một commit.
   * Lô nào nạp thêm nội dung sau đó thì gọi lại `revealAll(node)` cho riêng phần mới.
   */
  useEffect(() => revealAll(), []);

  return (
    <div className="flex min-h-dvh flex-col">
      {/*
        Liên kết bỏ qua điều hướng: người dùng bàn phím và screen reader không phải nghe lại
        toàn bộ menu ở mỗi trang. Nó ẩn cho tới khi được focus.
      */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-full focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white"
      >
        {copy.nav.skipToContent}
      </a>

      <Header />
      {/* `id="main"` là đích của skip link; `tabIndex={-1}` để focus nhảy được vào đây. */}
      <main id="main" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
